<script setup lang="ts">
import { computed } from 'vue'
import type { EnrichedHoldingPrice } from '../api/client'

const props = defineProps<{
  imrWindow: number | null
  imrWeightedZscore: number | null
  imrBreadthRich: number | null
  imrBreadthCheap: number | null
  imrMomentumCount: number
  holdingsCount: number
  holdingsDetail: EnrichedHoldingPrice[]
  imrAsOf: string | null
}>()

type Row = EnrichedHoldingPrice & { absZ: number }

const includedRows = computed<Row[]>(() => {
  const rows = props.holdingsDetail
    .filter(h => h.imr_zscore != null)
    .map(h => ({ ...h, absZ: Math.abs(h.imr_zscore as number) }))
  rows.sort((a, b) => b.absZ - a.absZ)
  return rows
})

const excludedRows = computed<EnrichedHoldingPrice[]>(() => {
  const rows = props.holdingsDetail.filter(h => h.imr_zscore == null)
  rows.sort((a, b) => b.weight - a.weight)
  return rows
})

const includedWeightPct = computed(() => {
  const w = includedRows.value.reduce((s, r) => s + r.weight, 0)
  return (w * 100)
})

const excludedWeightPct = computed(() => {
  const w = excludedRows.value.reduce((s, r) => s + r.weight, 0)
  return (w * 100)
})

// Reason → human label. Drives both the badge text and the grouped section
// headers in the excluded table.
const REASON_LABEL: Record<string, string> = {
  no_live_price: 'No live price at 20:00 UTC capture',
  warmup_incomplete: 'Warm-up incomplete at this window',
  engine_unavailable: 'IMR engine produced no composite',
  basket_added_after_warmup: 'Added to basket after IMR warm-up — self-heal in flight',
}

function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Unknown'
  return REASON_LABEL[reason] ?? reason
}

const excludedByReason = computed(() => {
  const groups: Record<string, EnrichedHoldingPrice[]> = {}
  for (const r of excludedRows.value) {
    const key = r.imr_excluded_reason ?? 'unknown'
    if (!groups[key]) groups[key] = []
    groups[key].push(r)
  }
  return groups
})

function fmt(n: number | null, digits = 2): string {
  if (n == null || Number.isNaN(n)) return '—'
  return n.toFixed(digits)
}

function fmtPct(n: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—'
  return (n * 100).toFixed(digits) + '%'
}

function zClass(z: number | null | undefined): string {
  if (z == null) return ''
  if (z > 1) return 'z-rich'
  if (z < -1) return 'z-cheap'
  return 'z-mid'
}
</script>

<template>
  <div class="imr-popover" data-testid="imr-breakdown-popover">
    <div class="ip-title">
      IMR composition &amp; coverage
      <span v-if="imrAsOf" class="ip-asof">as of {{ imrAsOf }}</span>
    </div>

    <!-- Composition formula -->
    <div class="ip-section">
      <div class="ip-section-title">Composition (window: {{ imrWindow ?? '?' }}d)</div>
      <div class="ip-formula">
        <div>
          <code>wz = Σ(weight × z) / Σ(weight)</code> over the {{ includedRows.length }} included holding(s)
        </div>
        <div class="ip-stat-row">
          <span class="ip-stat-label">Weighted z (wz)</span>
          <span class="ip-stat-value" :class="zClass(imrWeightedZscore)">{{ fmt(imrWeightedZscore) }}</span>
        </div>
        <div class="ip-stat-row">
          <span class="ip-stat-label">Rich (z &gt; +1)</span>
          <span class="ip-stat-value z-rich">{{ fmtPct(imrBreadthRich) }} of included weight</span>
        </div>
        <div class="ip-stat-row">
          <span class="ip-stat-label">Cheap (z &lt; −1)</span>
          <span class="ip-stat-value z-cheap">{{ fmtPct(imrBreadthCheap) }} of included weight</span>
        </div>
        <div class="ip-stat-row">
          <span class="ip-stat-label">Coverage</span>
          <span class="ip-stat-value">
            {{ imrMomentumCount }} / {{ holdingsCount }} holdings
            ({{ fmt(includedWeightPct, 1) }}% by weight)
          </span>
        </div>
      </div>
    </div>

    <!-- Included table -->
    <div class="ip-section" v-if="includedRows.length">
      <div class="ip-section-title">
        Included ({{ includedRows.length }}) — sorted by |z|
      </div>
      <table class="ip-table" data-testid="imr-included-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th class="num">Wt%</th>
            <th class="num">z</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in includedRows" :key="r.ticker">
            <td class="ticker">{{ r.ticker }}</td>
            <td class="num">{{ fmt(r.weight * 100, 2) }}</td>
            <td class="num" :class="zClass(r.imr_zscore)">{{ fmt(r.imr_zscore) }}</td>
            <td class="src">{{ r.source }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Excluded table grouped by reason -->
    <div class="ip-section" v-if="excludedRows.length">
      <div class="ip-section-title">
        Excluded ({{ excludedRows.length }}, {{ fmt(excludedWeightPct, 1) }}% of basket weight)
      </div>
      <div
        v-for="(rows, reason) in excludedByReason"
        :key="reason"
        class="ip-reason-group"
        :data-testid="`imr-excluded-group-${reason}`"
      >
        <div class="ip-reason-header">
          <span class="ip-reason-badge" :class="`reason-${reason}`">{{ reason }}</span>
          <span class="ip-reason-label">{{ reasonLabel(reason as string) }}</span>
          <span class="ip-reason-count">{{ rows.length }}</span>
        </div>
        <table class="ip-table compact">
          <thead>
            <tr>
              <th>Ticker</th>
              <th class="num">Wt%</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.ticker">
              <td class="ticker">{{ r.ticker }}</td>
              <td class="num">{{ fmt(r.weight * 100, 2) }}</td>
              <td class="src">{{ r.source }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!includedRows.length && !excludedRows.length" class="ip-empty">
      No holdings detail available — the enriched feed has not loaded yet.
    </div>
  </div>
</template>

<style scoped>
.imr-popover {
  background: #1a2a1a;
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  padding: 12px 14px;
  font-size: 11px;
  line-height: 1.5;
  color: #ccc;
  margin: 4px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  animation: imrPopFade 0.15s ease;
}
@keyframes imrPopFade {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.ip-title {
  font-size: 13px;
  font-weight: 700;
  color: #eee;
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.ip-asof {
  font-size: 10px;
  font-weight: 400;
  color: #888;
}
.ip-section {
  margin-bottom: 12px;
}
.ip-section:last-child {
  margin-bottom: 0;
}
.ip-section-title {
  font-size: 12px;
  font-weight: 600;
  color: #ddd;
  margin-bottom: 6px;
  border-bottom: 1px solid #2a3a2a;
  padding-bottom: 3px;
}
.ip-formula code {
  background: #0d1f0d;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 11px;
  color: #9cdcfe;
}
.ip-stat-row {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
}
.ip-stat-label {
  color: #aaa;
}
.ip-stat-value {
  color: #eee;
  font-weight: 600;
}
.ip-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.ip-table th {
  text-align: left;
  color: #888;
  font-weight: 500;
  border-bottom: 1px solid #2a3a2a;
  padding: 3px 6px;
}
.ip-table td {
  padding: 2px 6px;
  border-bottom: 1px solid #1a2a1a;
}
.ip-table th.num,
.ip-table td.num {
  text-align: right;
  font-family: 'Consolas', 'Monaco', monospace;
}
.ip-table td.ticker {
  font-weight: 600;
  color: #eee;
}
.ip-table td.src {
  color: #aaa;
  font-size: 10px;
}
.ip-table.compact td,
.ip-table.compact th {
  padding: 1px 6px;
}
.z-rich { color: #3fb950; }
.z-cheap { color: #f85149; }
.z-mid { color: #ccc; }
.ip-reason-group {
  margin-top: 8px;
}
.ip-reason-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
}
.ip-reason-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 2px;
  font-size: 10px;
  font-family: 'Consolas', 'Monaco', monospace;
  background: #2a2a2a;
  color: #ddd;
  border: 1px solid #444;
}
.reason-no_live_price {
  background: #4a2a2a;
  border-color: #6a3a3a;
  color: #f5a5a5;
}
.reason-warmup_incomplete {
  background: #3a3a1a;
  border-color: #5a5a2a;
  color: #e0d080;
}
.reason-engine_unavailable {
  background: #1a1a4a;
  border-color: #2a2a6a;
  color: #a5a5f5;
}
.reason-basket_added_after_warmup {
  background: #2a3a4a;
  border-color: #3a4a5a;
  color: #9cc5e0;
}
.ip-reason-label {
  font-size: 11px;
  color: #bbb;
}
.ip-reason-count {
  margin-left: auto;
  font-size: 10px;
  color: #888;
  font-family: 'Consolas', 'Monaco', monospace;
}
.ip-empty {
  color: #888;
  font-style: italic;
}
</style>
